import { Component, Input, OnInit, Output, EventEmitter, inject, input } from '@angular/core';
import { Router } from '@angular/router';
import { IAppPage } from 'src/app/models/navigation/navigation.interface';
import { FeatureFlagsService } from 'src/app/services/infra/featureFlags/feature-flags.service';
import { FeatureFlagTypes } from 'src/app/models/featureFlag/featureFlagsTypes.enum';

@Component({
  selector: 'app-nav-menu-item',
  templateUrl: './nav-menu-item.component.html',
  styleUrls: ['./nav-menu-item.component.scss'],
  host: {
    '[class.expanded]': 'menuExpanded()',
    '[class.parent-expanded]': 'parentExpanded',
    '[class.selected]': 'selected',
    '[class.active]': 'active',
  },
})
export class NavMenuItemComponent implements OnInit {
  @Input() public appPage: IAppPage;
  @Input() public index = 0;

  public readonly menuExpanded = input<boolean>(true);

  get parentExpanded() {
    return this.appPage?.isExpanded ?? false;
  }

  get selected() {
    return this.appPage?.selected ?? false;
  }

  get active() {
    return this.appPage?.isActive ?? false;
  }

  @Output() public parentClick = new EventEmitter<IAppPage>();
  @Output() public expansionToggle = new EventEmitter<IAppPage>();

  public baseUrl = '';
  public externalEndpoint = false;
  public reactRoute = false;

  private readonly _featureFlagsService = inject(FeatureFlagsService);

  constructor(private readonly _router: Router) {}

  ngOnInit(): void {
    this.externalEndpoint = this.appPage?.navigationTo?.startsWith('http') ?? false;
    this.reactRoute = this.appPage?.isReactRoute ?? false;
  }

  get reactNavigationUrl(): string {
    const query = this.appPage.navigationQuery;
    if (!query || Object.keys(query).length === 0) {
      return this.appPage.navigationTo;
    }
    const params = new URLSearchParams(query as Record<string, string>).toString();
    return `${this.appPage.navigationTo}?${params}`;
  }

  /**
   * Determines if the menu item should be visible.
   * Hides the item if:
   * 1. The item itself is marked as hidden
   * 2. The item has children but all children are hidden
   */
  get shouldShowMenuItem(): boolean {
    if (this.appPage?.hidden) {
      return false;
    }

    if (this.appPage?.children && this.appPage.children.length > 0) {
      return this.appPage.children.some((child) => !child.hidden);
    }
    return true;
  }

  public onParentClick(): void {
    this.parentClick.emit(this.appPage);
  }

  public onExpandToggle(): void {
    if (!this.menuExpanded()) {
      return;
    }

    this.appPage.isExpanded = !this.appPage.isExpanded;
    this.expansionToggle.emit(this.appPage);
  }

  public onChildExpansionToggle(appPage: IAppPage): void {
    // Bubble up expansion events from child components
    this.expansionToggle.emit(appPage);
  }

  public isChildExternalEndpoint(child: IAppPage): boolean {
    return child.navigationTo?.startsWith('http') ?? false;
  }

  public isIssuesPage(): boolean {
    return this.appPage?.displayTitleLabelPath === 'menu.issuesPage';
  }

  public shouldShowPreviewBadge(): boolean {
    return (
      this.isIssuesPage() && !this._featureFlagsService.isFeatureEnabled(FeatureFlagTypes.ReactHideIssuesPreviewBadge)
    );
  }
}
